import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, Calendar, Flag, User, Clock } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateTaskModal = ({ message, chatId, onClose, onSuccess, chatParticipants = [] }) => {
  const { token, user } = useAuth();
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  
  // Get today's date in YYYY-MM-DD format
  const getTodayString = () => {
    const date = new Date();
    return date.toISOString().split('T')[0];
  };
  
  const [formData, setFormData] = useState({
    title: message?.content?.substring(0, 100) || '',
    description: '',
    priority: 'medium',
    dueDate: getTodayString(), // Default to today
    assignedTo: user?.id || ''
  });
  const [showCustomDate, setShowCustomDate] = useState(false);

  // Get today and tomorrow dates in YYYY-MM-DD format
  const getDateString = (daysFromNow = 0) => {
    const date = new Date();
    date.setDate(date.getDate() + daysFromNow);
    return date.toISOString().split('T')[0];
  };

  const today = getDateString(0);
  const tomorrow = getDateString(1);

  const handleQuickDate = (date) => {
    setFormData({ ...formData, dueDate: date });
    setShowCustomDate(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Please enter a task title');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/tasks`,
        {
          message_id: message?.id || null,
          chat_id: chatId,
          title: formData.title,
          description: formData.description || undefined,
          priority: formData.priority,
          due_date: formData.dueDate || undefined,
          assigned_to: formData.assignedTo || undefined
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Task created!');
      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error('Failed to create task');
    } finally {
      setLoading(false);
    }
  };

  const getDueDateLabel = () => {
    if (!formData.dueDate) return 'No due date';
    if (formData.dueDate === today) return 'Today';
    if (formData.dueDate === tomorrow) return 'Tomorrow';
    return new Date(formData.dueDate).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: formData.dueDate.split('-')[0] !== new Date().getFullYear().toString() ? 'numeric' : undefined
    });
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className={`rounded-3xl p-6 max-w-md w-full shadow-card ${
            isDarkMode ? 'bg-gray-900' : 'bg-white'
          }`}
          data-testid="create-task-modal"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isDarkMode ? 'bg-[#D4AF37]/20' : 'bg-primary/10'
              }`}>
                <CheckCircle size={20} className="text-[#D4AF37]" />
              </div>
              <h3 className={`font-heading text-xl ${isDarkMode ? 'text-white' : 'text-text-primary'}`}>
                Create Task
              </h3>
            </div>
            <button
              onClick={onClose}
              className={`h-8 w-8 rounded-full transition-colors flex items-center justify-center ${
                isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-secondary-DEFAULT text-text-secondary'
              }`}
            >
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Task Title */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-text-primary'}`}>
                Task Title *
              </label>
              <input
                data-testid="task-title-input"
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className={`w-full h-12 border-none rounded-xl px-4 focus:ring-1 focus:ring-[#D4AF37] ${
                  isDarkMode 
                    ? 'bg-gray-800 text-white placeholder:text-gray-500' 
                    : 'bg-secondary-DEFAULT text-text-primary'
                }`}
                placeholder="What needs to be done?"
                required
                autoFocus
              />
            </div>

            {/* Quick Due Date Buttons */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-text-primary'}`}>
                Due Date
              </label>
              <div className="flex gap-2 flex-wrap">
                <button
                  type="button"
                  data-testid="due-today-btn"
                  onClick={() => handleQuickDate(today)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    formData.dueDate === today
                      ? 'bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white'
                      : isDarkMode 
                        ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' 
                        : 'bg-secondary-DEFAULT text-text-primary hover:bg-border-DEFAULT'
                  }`}
                >
                  Today
                </button>
                <button
                  type="button"
                  data-testid="due-tomorrow-btn"
                  onClick={() => handleQuickDate(tomorrow)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    formData.dueDate === tomorrow
                      ? 'bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white'
                      : isDarkMode 
                        ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' 
                        : 'bg-secondary-DEFAULT text-text-primary hover:bg-border-DEFAULT'
                  }`}
                >
                  Tomorrow
                </button>
                <button
                  type="button"
                  data-testid="due-custom-btn"
                  onClick={() => setShowCustomDate(!showCustomDate)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    formData.dueDate && formData.dueDate !== today && formData.dueDate !== tomorrow
                      ? 'bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white'
                      : isDarkMode 
                        ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' 
                        : 'bg-secondary-DEFAULT text-text-primary hover:bg-border-DEFAULT'
                  }`}
                >
                  <Calendar size={14} className="inline mr-1" />
                  {formData.dueDate && formData.dueDate !== today && formData.dueDate !== tomorrow 
                    ? getDueDateLabel() 
                    : 'Custom'}
                </button>
                {formData.dueDate && (
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, dueDate: '' })}
                    className={`px-3 py-2 rounded-xl text-sm transition-all ${
                      isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-text-tertiary hover:text-text-secondary'
                    }`}
                  >
                    Clear
                  </button>
                )}
              </div>
              
              {/* Custom Date Picker */}
              {showCustomDate && (
                <div className="mt-3">
                  <input
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => {
                      setFormData({ ...formData, dueDate: e.target.value });
                      setShowCustomDate(false);
                    }}
                    min={today}
                    className={`w-full h-12 border-none rounded-xl px-4 focus:ring-1 focus:ring-[#D4AF37] ${
                      isDarkMode 
                        ? 'bg-gray-800 text-white' 
                        : 'bg-secondary-DEFAULT text-text-primary'
                    }`}
                  />
                </div>
              )}
            </div>

            {/* Priority */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-text-primary'}`}>
                Priority
              </label>
              <div className="flex gap-2">
                {['low', 'medium', 'high'].map((priority) => (
                  <button
                    key={priority}
                    type="button"
                    onClick={() => setFormData({ ...formData, priority })}
                    className={`flex-1 py-2 rounded-xl text-sm font-medium transition-all capitalize ${
                      formData.priority === priority
                        ? priority === 'high' 
                          ? 'bg-red-500 text-white'
                          : priority === 'medium'
                            ? 'bg-yellow-500 text-white'
                            : 'bg-green-500 text-white'
                        : isDarkMode 
                          ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' 
                          : 'bg-secondary-DEFAULT text-text-primary hover:bg-border-DEFAULT'
                    }`}
                  >
                    {priority}
                  </button>
                ))}
              </div>
            </div>

            {/* Assign To (if in a chat with participants) */}
            {chatParticipants && chatParticipants.length > 0 && (
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-text-primary'}`}>
                  Assign To
                </label>
                <select
                  value={formData.assignedTo}
                  onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
                  className={`w-full h-12 border-none rounded-xl px-4 focus:ring-1 focus:ring-[#D4AF37] ${
                    isDarkMode 
                      ? 'bg-gray-800 text-white' 
                      : 'bg-secondary-DEFAULT text-text-primary'
                  }`}
                >
                  <option value={user?.id}>Self (Me)</option>
                  {chatParticipants.filter(p => p.id !== user?.id).map((participant) => (
                    <option key={participant.id} value={participant.id}>
                      {participant.display_name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Description (Optional) */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-text-primary'}`}>
                Description (optional)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className={`w-full h-20 border-none rounded-xl px-4 py-3 resize-none focus:ring-1 focus:ring-[#D4AF37] ${
                  isDarkMode 
                    ? 'bg-gray-800 text-white placeholder:text-gray-500' 
                    : 'bg-secondary-DEFAULT text-text-primary'
                }`}
                placeholder="Add details..."
              />
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={loading}
                data-testid="create-task-submit-btn"
                className="flex-1 h-12 rounded-xl bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white font-medium hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Task'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className={`h-12 px-6 rounded-xl font-medium transition-colors ${
                  isDarkMode 
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' 
                    : 'bg-secondary-DEFAULT text-text-primary hover:bg-border-DEFAULT'
                }`}
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default CreateTaskModal;
